import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';  // ✅ ADD useLocation
import toast from 'react-hot-toast';
import API from '../services/api';
// ✅ Flow Setup Request Card Component
// ✅ Flow Setup Request Card Component
function FlowSetupRequestCard({ req, onUpdate }) {
  const [adminNotes, setAdminNotes] = useState('');
  const [fileMode, setFileMode] = useState('link'); // 'link' | 'upload'
  const [flowFileUrl, setFlowFileUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  // ✅ Submit Flow File & Mark as Completed
  const handleSubmit = async () => {
    if (fileMode === 'link' && !flowFileUrl.trim()) {
      toast.error('Please enter a Google Drive / file link, or switch to file upload.');
      return;
    }
    if (fileMode === 'upload' && !selectedFile) {
      toast.error('Please select a file to upload.');
      return;
    }

    setSubmitting(true);
    const toastId = toast.loading('Submitting flow file...');
    try {
      let finalFileUrl = flowFileUrl.trim();

      // If upload mode, upload file first
      if (fileMode === 'upload' && selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);

        const uploadRes = await API.post('/admin/flow-setup-requests/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        finalFileUrl = uploadRes.data.data.fileUrl;
      }

      await API.patch(`/admin/flow-setup-requests/${req.id}/status`, {
        status: 'completed',
        adminNotes: adminNotes.trim(),
        flowFileUrl: finalFileUrl
      });

      toast.success('Flow file sent & request marked completed!', { id: toastId });
      setAdminNotes('');
      setFlowFileUrl('');
      setSelectedFile(null);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit flow file', { id: toastId });
    }
    setSubmitting(false);
  };

  // ❌ Reject Request
  const handleReject = async () => {
    if (!window.confirm('Are you sure you want to reject this request?')) return;
    setRejecting(true);
    const toastId = toast.loading('Rejecting request...');
    try {
      await API.patch(`/admin/flow-setup-requests/${req.id}/status`, {
        status: 'rejected',
        adminNotes: adminNotes.trim(),
        flowFileUrl: null
      });
      toast.success('Request rejected successfully', { id: toastId });
      setAdminNotes('');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject request', { id: toastId });
    }
    setRejecting(false);
  };

  const statusColors = {
    pending: '#f59e0b',
    in_progress: '#3b82f6',
    completed: '#22c55e',
    rejected: '#ef4444'
  };

  return (
    <div style={{ border: '1.5px solid #e2e8f0', borderRadius: '12px', padding: '18px', marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{
          background: statusColors[req.status] + '20',
          color: statusColors[req.status],
          padding: '4px 12px', borderRadius: '20px',
          fontSize: '12px', fontWeight: '700'
        }}>
          {req.status === 'pending' ? '⏳ Pending' :
            req.status === 'in_progress' ? '🔄 In Progress' :
              req.status === 'completed' ? '✅ Completed' : '❌ Rejected'}
        </span>
        <span style={{ fontSize: '12px', color: '#94a3b8' }}>
          {new Date(req.created_at).toLocaleString()}
        </span>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>PLAN</div>
        <div style={{ fontSize: '14px', color: '#1e293b' }}>{req.plan_name}</div>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>BUSINESS REQUIREMENTS</span>
          <button
            onClick={() => {
              const el = document.getElementById(`req-text-${req.id}`);
              const btn = document.getElementById(`req-btn-${req.id}`);
              if (el.style.maxHeight === 'none') {
                el.style.maxHeight = '80px';
                el.style.overflow = 'hidden';
                btn.textContent = '🔽 Show More';
              } else {
                el.style.maxHeight = 'none';
                el.style.overflow = 'visible';
                btn.textContent = '🔼 Show Less';
              }
            }}
            id={`req-btn-${req.id}`}
            style={{
              fontSize: '11px', fontWeight: '600', color: '#3b82f6',
              background: 'none', border: '1px solid #bfdbfe',
              borderRadius: '6px', padding: '2px 8px', cursor: 'pointer'
            }}
          >
            🔽 Show More
          </button>
        </div>
        <div
          id={`req-text-${req.id}`}
          style={{
            background: '#f8fafc', borderRadius: '8px', padding: '10px 12px',
            fontSize: '13px', color: '#334155', lineHeight: '1.6',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            maxHeight: '80px', overflow: 'hidden',
            transition: 'max-height 0.3s ease'
          }}
        >
          {req.business_requirements}
        </div>
      </div>

      {req.admin_notes && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>ADMIN NOTES</div>
          <div style={{ fontSize: '13px', color: '#475569' }}>{req.admin_notes}</div>
        </div>
      )}

      {req.flow_file_url && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>FLOW FILE</div>
          <a href={req.flow_file_url} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: '13px', fontWeight: '600' }}>
            📥 {req.flow_file_url}
          </a>
        </div>
      )}

      {req.status !== 'completed' && (
        <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '14px', marginTop: '8px' }}>
          <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '700', marginBottom: '10px' }}>
            RESPOND TO FLOW SETUP REQUEST
          </div>

          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
              Admin Notes (optional)
            </label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add notes for the client..."
              rows={2}
              className="settings-form-textarea"
            />
          </div>

          {/* Mode Switcher: Link vs Upload */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button
              type="button"
              onClick={() => setFileMode('link')}
              style={{
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                border: fileMode === 'link' ? '1.5px solid #2563eb' : '1px solid #d1d5db',
                background: fileMode === 'link' ? '#eff6ff' : '#f8fafc',
                color: fileMode === 'link' ? '#1d4ed8' : '#64748b'
              }}
            >
              🔗 Drive / File Link
            </button>
            <button
              type="button"
              onClick={() => setFileMode('upload')}
              style={{
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                border: fileMode === 'upload' ? '1.5px solid #2563eb' : '1px solid #d1d5db',
                background: fileMode === 'upload' ? '#eff6ff' : '#f8fafc',
                color: fileMode === 'upload' ? '#1d4ed8' : '#64748b'
              }}
            >
              📤 Upload from System
            </button>
          </div>

          {fileMode === 'link' ? (
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                Flow File URL (Google Drive / Dropbox link) <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={flowFileUrl}
                onChange={(e) => setFlowFileUrl(e.target.value)}
                placeholder="https://drive.google.com/..."
                className="settings-form-input"
              />
            </div>
          ) : (
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                Select File From Your System <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="file"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="settings-form-input"
              />
              {selectedFile && (
                <p style={{ fontSize: '12px', color: '#166534', marginTop: '4px', fontWeight: '500' }}>
                  📎 Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
          )}

          {/* Action Buttons: Submit & Reject */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={handleSubmit}
              disabled={submitting || rejecting}
              className="adm-btn adm-btn-success"
              style={{ minWidth: '180px' }}
            >
              {submitting ? '⏳ Submitting...' : '✅ Submit & Send to Client'}
            </button>

            <button
              onClick={handleReject}
              disabled={submitting || rejecting}
              className="adm-btn adm-btn-danger"
            >
              {rejecting ? '⏳ Rejecting...' : '❌ Reject'}
            </button>
          </div>
        </div>
      )}

      {req.status === 'completed' && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '10px 14px', marginTop: '8px' }}>
          <p style={{ margin: 0, fontSize: '13px', color: '#166534', fontWeight: '600' }}>
            ✅ Request completed on {new Date(req.admin_replied_at || req.updated_at).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}

function OrganizationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();  // ✅ ADD THIS LINE
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfigureForm, setShowConfigureForm] = useState(false);
  const [deletingConfig, setDeletingConfig] = useState(false);
  const [configuringWA, setConfiguringWA] = useState(false);
  const [waConfigData, setWaConfigData] = useState({
    phoneNumberId: '', accessToken: '', businessAccountId: '', phoneNumber: '', verifyToken: ''
  });
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const loadOrganizationDetails = useCallback(async () => {
    try {
      const response = await API.get(`/admin/organization/${id}`);
      setData(response.data.data);
    } catch (error) {
      toast.error('Failed to load organization details');
      navigate('/admin/clients');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadOrganizationDetails(); }, [loadOrganizationDetails]);


  // ✅ ADD THIS
  useEffect(() => {
    if (data?.setupRequest && location.state?.scrollToSetup) {
      setTimeout(() => {
        const setupSection = document.getElementById('whatsapp-setup-section');
        if (setupSection) {
          setupSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [data, location.state]);
  const handleConfigureWhatsApp = async (e) => {
    e.preventDefault();
    setConfiguringWA(true);
    const toastId = toast.loading('Configuring WhatsApp...');
    try {
      await API.post(`/admin/organization/${id}/configure-whatsapp`, waConfigData);
      toast.success('✅ WhatsApp configured successfully!', { id: toastId, duration: 5000 });
      setShowConfigureForm(false);
      setWaConfigData({ phoneNumberId: '', accessToken: '', businessAccountId: '', phoneNumber: '', verifyToken: '' });
      await loadOrganizationDetails();
    } catch (error) {
      if (error.response?.data?.errorCode === 'PHONE_NUMBER_IN_USE') {
        toast.error('❌ Phone number already in use!', { id: toastId, duration: 6000 });
      } else {
        toast.error(error.response?.data?.message || 'Failed to configure', { id: toastId });
      }
    }
    setConfiguringWA(false);
  };

  const handleUpdateSetupStatus = async (newStatus) => {
    setUpdatingStatus(true);
    const toastId = toast.loading('Updating status...');
    try {
      await API.patch(`/admin/setup-requests/${data.setupRequest.id}/status`, {  // ✅ CORRECT
        status: newStatus,
        adminNotes: adminNotes  // ✅ MATCH BACKEND FIELD NAME
      });
      toast.success(`Status updated to: ${newStatus}`, { id: toastId });
      setSelectedStatus(''); setAdminNotes('');
      await loadOrganizationDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update', { id: toastId });
    }
    setUpdatingStatus(false);
  };

  const handleDeleteWhatsAppConfig = async () => {
    const confirmed = await new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'delete-confirm-overlay';
      overlay.innerHTML = `
        <div class="delete-confirm-modal">
          <div class="delete-confirm-header">
            <div class="delete-confirm-icon-wrapper">🗑️</div>
            <div>
              <h3 class="delete-confirm-title">Permanent Delete</h3>
              <p class="delete-confirm-subtitle">This action cannot be undone</p>
            </div>
          </div>
          <div class="delete-confirm-body">
            <p class="delete-confirm-desc">The following data will be permanently deleted:</p>
            <div class="delete-confirm-items">
              <div class="delete-confirm-item"><span>📵</span><span>WhatsApp credentials</span></div>
              <div class="delete-confirm-item"><span>📋</span><span>Setup request data</span></div>
              <div class="delete-confirm-item"><span>⚠️</span><span>User will need to resubmit everything</span></div>
            </div>
            <div class="delete-confirm-actions">
              <button id="cancel-delete" class="delete-confirm-btn-cancel">Cancel</button>
              <button id="confirm-delete" class="delete-confirm-btn-confirm">Yes, Delete</button>
            </div>
          </div>
        </div>`;
      document.body.appendChild(overlay);
      overlay.querySelector('#confirm-delete').onclick = () => { document.body.removeChild(overlay); resolve(true); };
      overlay.querySelector('#cancel-delete').onclick = () => { document.body.removeChild(overlay); resolve(false); };
      overlay.onclick = (e) => { if (e.target === overlay) { document.body.removeChild(overlay); resolve(false); } };
    });
    if (!confirmed) return;
    setDeletingConfig(true);
    const toastId = toast.loading('Deleting config...');
    try {
      await API.delete(`/admin/organization/${id}/whatsapp-config`);
      toast.success('✅ Config deleted!', { id: toastId, duration: 5000 });
      await loadOrganizationDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete', { id: toastId });
    }
    setDeletingConfig(false);
  };

  const getSetupStatusBadge = (status) => {
    const map = {
      pending: { cls: 'adm-badge adm-badge-pending', label: '⏳ Pending' },
      in_progress: { cls: 'adm-badge adm-badge-progress', label: '🔄 In Progress' },
      completed: { cls: 'adm-badge adm-badge-completed', label: '✅ Completed' },
      rejected: { cls: 'adm-badge adm-badge-rejected', label: '❌ Rejected' },
    };
    const s = map[status] || map.pending;
    return <span className={s.cls}>{s.label}</span>;
  };

  if (loading) return (
    <div className="loading-container">
      <div className="loading-content">
        <div className="loading-spinner">⏳</div>
        <p className="loading-text">Loading details...</p>
      </div>
    </div>
  );
  if (!data) return null;
  const { organization, users, flows, stats, whatsapp, aiAgent, recentMessages, setupRequest } = data;

  return (
    <div className="adm-page">

      {/* Header */}
      <div className="adm-header">
        <button onClick={() => navigate('/admin/clients')} className="adm-back-btn">← Back to clients</button>
        <div>
          <h1 className="adm-page-title">{organization.name}</h1>
          <p className="adm-page-subtitle">{organization.email} • Created {new Date(organization.created_at).toLocaleDateString()}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="adm-stats-grid">
        {[
          { label: 'Users', value: users.length },
          { label: 'Flows', value: flows.length },
          { label: 'Contacts', value: stats.contacts },
          { label: 'Messages (30d)', value: stats.messages.total_messages },
        ].map(s => (
          <div key={s.label} className="adm-stat-card">
            <div className="adm-stat-label">{s.label}</div>
            <div className="adm-stat-value">{s.value}</div>
          </div>
        ))}
      </div>
      {/* KYC / Business Details */}
      {(organization.account_type || organization.pan_number) && (
          <div className="adm-card">
              <h3 className="adm-card-title">📋 KYC / Business Details</h3>
              <div className="admin-setup-details-grid">
                  {[
                      ['Account Type', organization.account_type ? organization.account_type.charAt(0).toUpperCase() + organization.account_type.slice(1) : '—'],
                      ['Company Name', organization.company_name || '—'],
                      ['PAN Number', organization.pan_number || '—'],
                      ['GST Number', organization.gst_number || '—'],
                      ['Description', organization.company_description || '—'],
                  ].map(([label, value]) => (
                      <div key={label} className="admin-setup-detail-item">
                          <span className="admin-setup-detail-label">{label}</span>
                          <span className="admin-setup-detail-value">{value}</span>
                      </div>
                  ))}
              </div>
          </div>
      )}

      {/* WhatsApp + AI */}
      <div className="adm-two-col">

        {/* WhatsApp */}
        <div className="adm-card">
          <h3 className="adm-card-title">💬 WhatsApp Status</h3>
          {whatsapp ? (
            <>
              <div className="adm-badge-row">
                <span className="adm-badge adm-badge-completed">✅ Connected</span>
              </div>
              <div className="adm-info-list">
                <div className="adm-info-item"><strong>Phone:</strong> {whatsapp.phone_number || 'N/A'}</div>
                <div className="adm-info-item"><strong>Connected:</strong> {new Date(whatsapp.created_at).toLocaleDateString()}</div>
              </div>
              <div className="adm-btn-row">
                <button onClick={() => setShowConfigureForm(!showConfigureForm)} className="adm-btn adm-btn-primary">
                  {showConfigureForm ? '✕ Cancel' : '🔧 Reconfigure'}
                </button>
                <button onClick={handleDeleteWhatsAppConfig} disabled={deletingConfig} className="adm-btn adm-btn-danger">
                  {deletingConfig ? '⏳ Deleting...' : '🗑️ Delete Config'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="adm-not-connected">
                <div className="adm-not-connected-icon">❌</div>
                <div className="adm-not-connected-text">Not Connected</div>
              </div>
              <button onClick={() => setShowConfigureForm(!showConfigureForm)} className="adm-btn adm-btn-success adm-btn-full">
                {showConfigureForm ? '✕ Cancel' : '⚙️ Configure WhatsApp'}
              </button>
            </>
          )}

          {showConfigureForm && (
            <form onSubmit={handleConfigureWhatsApp} className="adm-config-form">
              <div className="adm-config-form-title">
                {whatsapp ? '🔧 Update Credentials' : '⚙️ Configure WhatsApp'}
              </div>
              {[
                { label: 'Phone Number ID', key: 'phoneNumberId', type: 'text', ph: '182327138287021', req: true },
                { label: 'Access Token', key: 'accessToken', type: 'password', ph: 'EAAUgMfAZAgrgBO...', req: true },
                { label: 'Business Account ID', key: 'businessAccountId', type: 'text', ph: 'Business Account ID' },
                { label: 'Phone Number', key: 'phoneNumber', type: 'text', ph: '+919876543210' },
                { label: 'Webhook Verify Token', key: 'verifyToken', type: 'text', ph: 'my_secret_token', req: true },
              ].map(f => (
                <div key={f.key} className="settings-form-group">
                  <label className="settings-form-label">
                    {f.label} {f.req && <span className="settings-form-required">*</span>}
                  </label>
                  <input
                    type={f.type}
                    value={waConfigData[f.key]}
                    onChange={e => setWaConfigData({ ...waConfigData, [f.key]: e.target.value })}
                    placeholder={f.ph}
                    className="settings-form-input"
                    required={!!f.req}
                  />
                </div>
              ))}
              <div className="adm-btn-row">
                <button type="submit" disabled={configuringWA} className="adm-btn adm-btn-success">
                  {configuringWA ? '⏳ Saving...' : '✅ Save & Configure'}
                </button>
                <button type="button" onClick={() => setShowConfigureForm(false)} className="adm-btn adm-btn-outline">
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* AI Agent */}
        <div className="adm-card">
          <h3 className="adm-card-title">🤖 AI Agent Status</h3>
          {aiAgent ? (
            <>
              <div className="adm-badge-row">
                <span className={`adm-badge ${aiAgent.is_active ? 'adm-badge-completed' : 'adm-badge-pending'}`}>
                  {aiAgent.is_active ? '✅ Active' : '⏸️ Inactive'}
                </span>
              </div>
              <div className="adm-info-list">
                <div className="adm-info-item"><strong>Name:</strong> {aiAgent.name}</div>
                <div className="adm-info-item"><strong>Model:</strong> {aiAgent.model}</div>
                <div className="adm-info-item">
                  <strong>Managed:</strong>{' '}
                  <span className={aiAgent.is_managed ? 'adm-text-success' : 'adm-text-muted'}>
                    {aiAgent.is_managed ? '✅ Yes (ChatTech24 Credits)' : '❌ No (Own API Key)'}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="adm-flow-mode-box">
              <div className="adm-flow-mode-icon">📊</div>
              <div className="adm-flow-mode-text">Flow Mode</div>
            </div>
          )}
        </div>
      </div>

      {/* Setup Request */}
      {setupRequest && (
        <div className="adm-card" id="whatsapp-setup-section">
          <h3 className="adm-card-title">📋 WhatsApp Setup Request</h3>

          <div className="adm-setup-meta">
            <div className="adm-setup-meta-left">
              {getSetupStatusBadge(setupRequest.status)}
              <span className="adm-meta-text">Submitted: {new Date(setupRequest.created_at).toLocaleString()}</span>
            </div>
            {setupRequest.setup_completed_at && (
              <span className="adm-meta-text">Completed: {new Date(setupRequest.setup_completed_at).toLocaleString()}</span>
            )}
          </div>

          <div className="admin-setup-details-grid">
            {[
              ['Business Name', setupRequest.business_name],
              ['Business Phone', setupRequest.business_phone],
              ['Business Email', setupRequest.business_email],
              ['Website', setupRequest.business_website],
              ['Address', setupRequest.business_address],
              ['Use Case', setupRequest.use_case],
              ['Expected Volume', setupRequest.expected_volume],
              ['Meta Business Manager ID', setupRequest.meta_business_manager_id],
              ['Meta Account Email', setupRequest.meta_account_email],
              ['Meta Access Level', setupRequest.meta_account_access],
            ].map(([label, value]) => (
              <div key={label} className="admin-setup-detail-item">
                <span className="admin-setup-detail-label">{label}</span>
                <span className="admin-setup-detail-value">{value || '—'}</span>
              </div>
            ))}
          </div>

          {setupRequest.business_description && (
            <div className="adm-info-box adm-info-box-gray">
              <p className="adm-info-box-label">Business Description</p>
              <p className="adm-info-box-text">{setupRequest.business_description}</p>
            </div>
          )}

          {setupRequest.admin_notes && (
            <div className="adm-info-box adm-info-box-yellow">
              <p className="adm-info-box-label">Admin Notes</p>
              <p className="adm-info-box-text">{setupRequest.admin_notes}</p>
            </div>
          )}

          {setupRequest.status !== 'completed' && (
            <div className="admin-setup-status-panel">
              <p className="admin-setup-status-panel-title">🛠️ Update Setup Request Status</p>
              <div className="adm-status-btn-row">
                {['pending', 'in_progress', 'rejected'].map(s => (
                  <button
                    key={s}
                    onClick={() => setSelectedStatus(s)}
                    className={`adm-status-pick-btn ${selectedStatus === s ? 'adm-status-pick-active' : ''}`}
                  >
                    {s === 'pending' ? '⏳ Pending' : s === 'in_progress' ? '🔄 In Progress' : '❌ Reject'}
                  </button>
                ))}
              </div>
              <div className="settings-form-group">
                <label className="settings-form-label">Admin Notes (optional)</label>
                <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)}
                  placeholder="Add notes for the client..." rows="2" className="settings-form-textarea" />
              </div>
              <div className="adm-btn-row">
                {selectedStatus && (
                  <button onClick={() => handleUpdateSetupStatus(selectedStatus)} disabled={updatingStatus} className="adm-btn adm-btn-success">
                    {updatingStatus ? '⏳ Updating...' : `✅ Confirm: ${selectedStatus.replace('_', ' ')}`}
                  </button>
                )}
                {!whatsapp && (
                  <button onClick={() => setShowConfigureForm(true)} className="adm-btn adm-btn-success">
                    ⚙️ Configure WhatsApp → Mark Complete
                  </button>
                )}
                {!whatsapp && setupRequest && (
                  <button onClick={handleDeleteWhatsAppConfig} disabled={deletingConfig} className="adm-btn adm-btn-danger">
                    {deletingConfig ? '⏳ Deleting...' : '🗑️ Delete Setup Request'}
                  </button>
                )}
                {whatsapp && setupRequest.status !== 'completed' && (
                  <button onClick={() => handleUpdateSetupStatus('completed')} disabled={updatingStatus} className="adm-btn adm-btn-success">
                    ✅ Mark as Completed
                  </button>
                )}
              </div>
            </div>
          )}

          {setupRequest.status === 'completed' && (
            <div className="adm-info-box adm-info-box-green">
              <p className="adm-info-box-label">✅ Setup Completed</p>
              {setupRequest.setup_completed_by && (
                <p className="adm-info-box-text">
                  By admin ID: {setupRequest.setup_completed_by} on {new Date(setupRequest.setup_completed_at).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {/* Flow Setup Requests */}
      {data.flowSetupRequests && data.flowSetupRequests.length > 0 && (
        <div className="adm-card">
          <h3 className="adm-card-title">🛠️ Flow Setup Requests ({data.flowSetupRequests.length})</h3>
          {data.flowSetupRequests.map((req) => (
            <FlowSetupRequestCard
              key={req.id}
              req={req}
              onUpdate={loadOrganizationDetails}
            />
          ))}
        </div>
      )}
      {/* Invoices */}
      {data.invoices && data.invoices.length > 0 && (
        <div className="adm-card">
          <h3 className="adm-card-title">🧾 Invoices ({data.invoices.length})</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Invoice #', 'Plan', 'Billing', 'Amount', 'Date', 'Valid Till', 'Status', ''].map((h, i) => (
                    <th key={i} style={{
                      padding: '10px 14px', textAlign: 'left',
                      fontSize: '11px', fontWeight: '700', color: '#475569',
                      textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
                      whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{
                        fontFamily: 'monospace', fontSize: '12px', fontWeight: '700',
                        color: '#1e3a5f', background: '#eff6ff',
                        padding: '2px 7px', borderRadius: '5px',
                      }}>
                        {inv.invoice_number}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>
                      {inv.plan_name}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: '12px', color: '#64748b', textTransform: 'capitalize' }}>
                      {inv.billing_cycle}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: '14px', fontWeight: '700', color: '#065f46' }}>
                      ₹{parseFloat(inv.amount).toLocaleString('en-IN')}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {new Date(inv.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {new Date(inv.plan_end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        <span style={{
                          background: '#dcfce7', color: '#166534',
                          padding: '3px 8px', borderRadius: '20px',
                          fontSize: '11px', fontWeight: '700', display: 'inline-block',
                        }}>✓ Paid</span>
                        {inv.subscription_status && inv.subscription_status !== 'active' && (
                          <span style={{
                            background: inv.subscription_status === 'deleted_by_admin' ? '#fee2e2' : '#fef3c7',
                            color: inv.subscription_status === 'deleted_by_admin' ? '#991b1b' : '#92400e',
                            padding: '2px 7px', borderRadius: '20px',
                            fontSize: '10px', fontWeight: '600', display: 'inline-block',
                          }}>
                            {inv.subscription_status === 'deleted_by_admin' ? '🗑 Removed' : '⏰ Expired'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <button
                        onClick={async () => {
                          const confirmed = await new Promise((resolve) => {
                            const overlay = document.createElement('div');
                            overlay.className = 'delete-confirm-overlay';
                            overlay.innerHTML = `
        <div class="delete-confirm-modal">
          <div class="delete-confirm-header">
            <div class="delete-confirm-icon-wrapper">🧾</div>
            <div>
              <h3 class="delete-confirm-title">Delete Invoice</h3>
              <p class="delete-confirm-subtitle">This action cannot be undone</p>
            </div>
          </div>
          <div class="delete-confirm-body">
            <p class="delete-confirm-desc">Are you sure you want to permanently delete invoice <strong>${inv.invoice_number}</strong>?</p>
            <div class="delete-confirm-items">
              <div class="delete-confirm-item"><span>⚠️</span><span>Invoice record will be removed forever</span></div>
              <div class="delete-confirm-item"><span>📊</span><span>This cannot be recovered</span></div>
            </div>
            <div class="delete-confirm-actions">
              <button id="cancel-delete" class="delete-confirm-btn-cancel">Cancel</button>
              <button id="confirm-delete" class="delete-confirm-btn-confirm">Yes, Delete</button>
            </div>
          </div>
        </div>`;
                            document.body.appendChild(overlay);
                            overlay.querySelector('#confirm-delete').onclick = () => { document.body.removeChild(overlay); resolve(true); };
                            overlay.querySelector('#cancel-delete').onclick = () => { document.body.removeChild(overlay); resolve(false); };
                            overlay.onclick = (e) => { if (e.target === overlay) { document.body.removeChild(overlay); resolve(false); } };
                          });
                          if (!confirmed) return;
                          try {
                            await API.delete(`/admin/invoices/${inv.id}/permanent`);
                            toast.success('Invoice deleted permanently');
                            loadOrganizationDetails();
                          } catch (err) {
                            toast.error('Failed to delete invoice');
                          }
                        }}
                        style={{
                          background: '#fee2e2', color: '#dc2626',
                          border: '1px solid #fecaca', borderRadius: '6px',
                          padding: '5px 10px', fontSize: '11px',
                          fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Users */}
      <div className="adm-card">
        <h3 className="adm-card-title">👥 Users ({users.length})</h3>
        {users.length > 0 ? (
          <div className="org-list-items">
            {users.map(user => (
              <div key={user.id} className="org-list-item">
                <div>
                  <div className="org-list-item-name">{user.name}</div>
                  <div className="org-list-item-detail">{user.email}</div>
                </div>
                <div className="org-list-item-badges">
                  <span className={user.is_super_admin ? 'org-badge-admin' : 'org-badge-role'}>
                    {user.is_super_admin ? '👑 Super Admin' : user.role}
                  </span>
                  <span className={user.is_active ? 'org-badge-active' : 'org-badge-inactive'}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="org-list-empty">No users</p>}
      </div>

      {/* Flows */}
      <div className="adm-card">
        <h3 className="adm-card-title">🤖 Flows ({flows.length})</h3>
        {flows.length > 0 ? (
          <div className="org-list-items">
            {flows.map(flow => (
              <div key={flow.id} className="org-list-item">
                <div>
                  <div className="org-list-item-name">{flow.name}</div>
                  <div className="org-list-item-detail">{flow.description || 'No description'}</div>
                </div>
                <div className="org-list-item-badges">
                  <span className={flow.is_published ? 'org-badge-published' : 'org-badge-draft'}>
                    {flow.is_published ? '✅ Published' : '📝 Draft'}
                  </span>
                  <span className="org-list-item-date">Updated {new Date(flow.updated_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="org-list-empty">No flows</p>}
      </div>

      {/* Recent Messages */}
      {/* <div className="adm-card">
        <h3 className="adm-card-title">💬 Recent Messages (Last 20)</h3>
        {recentMessages?.length > 0 ? (
          <div className="org-messages-list">
            {recentMessages.map(msg => (
              <div key={msg.id} className={`org-message-item ${msg.direction === 'outgoing' ? 'org-message-item-outgoing' : 'org-message-item-incoming'}`}>
                <div className="org-message-header">
                  <span className="org-message-name">{msg.contact_name || msg.contact_phone}</span>
                  <span className="org-message-time">{new Date(msg.created_at).toLocaleString()}</span>
                </div>
                <div className="org-message-content" style={{
                  color: msg.content === '[Privacy Protected]' ? '#94a3b8' : 'inherit',
                  fontStyle: msg.content === '[Privacy Protected]' ? 'italic' : 'normal'
                }}>
                  {msg.content === '[Privacy Protected]' ? '🔒 Message content hidden for privacy' : msg.content}
                </div>
                <div className="org-message-meta">{msg.direction === 'outgoing' ? '📤 Sent' : '📥 Received'} • {msg.status}</div>
              </div>
            ))}
          </div>
        ) : <p className="org-messages-empty">No recent messages</p>}
      </div> */}
    </div>
  );
}


export default OrganizationDetails;